import * as React from 'react';
import type { PropsWithChildren } from 'react';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
import styled from 'styled-components';
import type { CmdbObject, CmdbObjectAttribute } from 'rest/cmdb-object-picker';
import type { OptionProps } from '@atlaskit/select';
import { components } from '@atlaskit/select';
import { colors, gridSize } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import type { CmdbObjectPickerOption } from '../cmdb-object-picker-dumb';
import { MemoizedInsightIcon } from '../insight-icon';

export const BoldMatchingText: React.FC<{ match: string; children: string }> = ({ match, children }) => {
    if (match === '') {
        return <>{children}</>;
    }

    // we need to escape special characters to avoid getting them interpreted in the RegExp group
    const escapedSpecialCharsInMatchQuery = match.replace(/[\\.,*+?^${}()|[\]]/g, '\\$&');
    const parts = children.split(new RegExp(`(${escapedSpecialCharsInMatchQuery})`, 'gi'));
    return (
        <>
            {parts.map((part, i) => {
                if (part.toLowerCase() === match.toLowerCase()) {
                    return <strong key={i}>{part}</strong>;
                }
                return <React.Fragment key={i}>{part}</React.Fragment>;
            })}
        </>
    );
};

export const getVisibleAttributes = (
    attributes: CmdbObjectAttribute[],
    attributesToDisplay: string[]
): CmdbObjectAttribute[] => {
    const notUndefined = (attr: CmdbObjectAttribute | undefined): attr is CmdbObjectAttribute => {
        return Boolean(attr);
    };
    return (
        attributesToDisplay
            .map((attrName) =>
                attrName === 'Label'
                    ? attributes.find((attr) => attr.objectTypeAttribute.label)
                    : attributes.find((attr) => attr.objectTypeAttribute.name === attrName)
            )
            // get only attributes that have valid displayValue
            .filter(notUndefined)
            .filter(
                (attribute) =>
                    attribute.objectAttributeValues.length > 0 &&
                    attribute.objectAttributeValues.every((value) => value && value.displayValue)
            )
    );
};

export const createAttributeString = (attributes: CmdbObjectAttribute[]): string => {
    return attributes
        .map((attribute) => attribute.objectAttributeValues.map((value) => value.displayValue).join(', '))
        .join(' | ');
};

export const createLabelString = (attributes: CmdbObjectAttribute[], defaultLabel: string): string => {
    return attributes[0]?.objectAttributeValues.map((v) => v.displayValue).join(', ') || defaultLabel;
};

export type Props = OptionProps<CmdbObjectPickerOption>;

export const CmdbSelectOption = (optionProps: PropsWithChildren<Props>) => {
    const { data, innerProps, selectProps } = optionProps;
    const { inputValue, filterAttributes } = selectProps;
    const object = data.cmdbObject as CmdbObject;
    const visibleAttributes = getVisibleAttributes(object.attributes, filterAttributes ?? []);
    const [, ...secondaryAttributes] = visibleAttributes;
    const labelAttribute = createLabelString(visibleAttributes, object.label);
    const secondaryAttributesDescription = createAttributeString(secondaryAttributes);
    const { Option } = components;
    const { ref, ...otherDivProps } = innerProps;

    return (
        <Option {...optionProps}>
            <Outer {...otherDivProps}>
                <Icon>
                    <MemoizedInsightIcon {...object.avatar.mediaClientConfig} />
                </Icon>
                <Content>
                    <PrimaryAttribute>
                        <BoldMatchingText match={inputValue ?? ''}>{labelAttribute}</BoldMatchingText>
                    </PrimaryAttribute>
                    <SecondaryAttributes>
                        <BoldMatchingText match={inputValue ?? ''}>{secondaryAttributesDescription}</BoldMatchingText>
                    </SecondaryAttributes>
                </Content>
            </Outer>
        </Option>
    );
};

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const Outer = styled.div`
    display: flex;
    padding: ${token('space.100', '8px')} ${token('space.200', '16px')};

    &:hover {
        background-color: ${token('elevation.surface.overlay.hovered', colors.N30)};
    }
`;

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const Icon = styled.div`
    margin-right: ${token('space.150', '12px')};
    flex-shrink: 0;
`;

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const Content = styled.div`
    color: ${token('color.text', colors.N800)};
`;

// eslint-disable-next-line rulesdir/no-styled-export, @atlaskit/ui-styling-standard/no-exported-styles, @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
export const PrimaryAttribute = styled.div`
    line-height: ${gridSize() * 3}px;
    font-weight: 500;
`;

// eslint-disable-next-line rulesdir/no-styled-export, @atlaskit/ui-styling-standard/no-exported-styles, @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
export const SecondaryAttributes = styled.div`
    font-size: ${gridSize() * 1.5}px;
    line-height: ${gridSize() * 2}px;
`;
